import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import ChartCard from './shared/ChartCard';
import { COLORS, apexFontFamily } from './shared/chartTokens';

/**
 * Renders a grouped vertical bar chart comparing overall completion %
 * between two locations across all selected financial years.
 *
 * @param {object}  props
 * @param {object}  props.chartData - `{ series: [{name, data}], categories: string[], primaryLocationName: string, compareLocationName: string }`
 * @param {boolean} props.loading   - loading state
 */
const LocationPerformanceComparisonChart = ({ chartData = null, loading = false }) => {
  const hasData = useMemo(() => {
    return (
      chartData &&
      Array.isArray(chartData.series) &&
      chartData.series.length > 0 &&
      Array.isArray(chartData.categories) &&
      chartData.categories.length > 0
    );
  }, [chartData]);

  const safeSeries = useMemo(() => {
    if (!hasData) return [];
    return chartData.series.map(s => ({
      ...s,
      data: Array.isArray(s?.data) ? s.data : [],
    }));
  }, [chartData, hasData]);

  const safeCategories = useMemo(() => {
    if (!hasData) return [];
    return chartData.categories;
  }, [chartData, hasData]);

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: { show: false },
      fontFamily: apexFontFamily,
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 5,
        borderRadiusApplication: 'end',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -22,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [COLORS.text],
        fontFamily: apexFontFamily,
      },
      formatter: (val) => `${val}%`,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: safeCategories,
      title: {
        text: 'Financial Year',
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.textMuted,
          fontFamily: apexFontFamily,
        },
      },
      labels: {
        style: {
          fontSize: '12px',
          colors: COLORS.text,
          fontWeight: 500,
          fontFamily: apexFontFamily,
        },
      },
      axisBorder: { show: true, color: '#E5E7EB' },
      axisTicks: { show: false },
    },
    yaxis: {
      title: {
        text: 'Completion (%)',
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.textMuted,
          fontFamily: apexFontFamily,
        },
      },
      labels: {
        formatter: (val) => `${val}%`,
        style: {
          fontSize: '11px',
          colors: COLORS.textMuted,
          fontFamily: apexFontFamily,
        },
      },
      min: 0,
      max: 100,
      tickAmount: 5,
    },
    colors: ['#6366F1', '#10B981'],
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.15,
        opacityFrom: 0.95,
        opacityTo: 0.85,
        stops: [0, 100],
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: apexFontFamily,
      labels: { colors: COLORS.textMuted },
      markers: { width: 12, height: 12, radius: 3 },
      itemMargin: { horizontal: 12, vertical: 6 },
    },
    grid: {
      show: true,
      borderColor: '#EEF2FF',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 20, bottom: 0, left: 10 },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => `${val}%`,
      },
      style: {
        fontSize: '13px',
        fontFamily: apexFontFamily,
      },
    },
  };

  return (
    <ChartCard
      title="Location Performance Comparison"
      icon="🌍"
      description="Overall data completion % per location across financial years"
      loading={loading}
      isEmpty={!hasData}
      skeletonType="bars"
      emptyIcon="🌍"
      emptyTitle="No Comparison Data Available"
      emptyMessage="Select primary and comparative locations to view comparison."
      accentColor="#6366F1"
      minHeight="480px"
    >
      {/* Chart */}
      <div style={{
        width: '100%',
        background: '#FAFBFF',
        borderRadius: '12px',
        border: '1px solid rgba(99,102,241,0.08)',
        padding: '20px',
      }}>
        <Chart
          options={chartOptions}
          series={safeSeries}
          type="bar"
          height={400}
        />
      </div>
    </ChartCard>
  );
};

export default LocationPerformanceComparisonChart;